import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft, Loader2, Zap, Shield, FileText, Camera, RotateCw, RotateCcw,
  Maximize2, Star, StickyNote, MessageSquare, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, HelpCircle, Mail, Printer, Truck, Clock,
  Award, Download, FileCheck, Search,
  Edit2, Save, Upload, User, ShoppingBag, Paperclip, Trash2, ScanSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import ImageInspector from '@/components/team/ImageInspector';
import { EmailCustomerCard } from '@/components/team/EmailCustomerCard';
import { IMAGE_UPLOAD_ACCEPT } from '@/lib/uploadAccept';

const STATUS_LABELS = {
  order_received: 'Order Received', in_review: 'In Review',
  more_images_requested: 'More Images Requested', images_received: 'Images Received',
  authentic: 'Authentic', counterfeit: 'Counterfeit',
  appraisal_complete: 'Appraisal Complete', unable_to_determine: 'Unable to Determine',
  refunded: 'Refunded', revoked: 'Revoked',
};

export default function ItemDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { isFullAdmin } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [imageMsg, setImageMsg] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [customCertCode, setCustomCertCode] = useState('');
  const [requestAttachment, setRequestAttachment] = useState(null);
  const [imageSlots, setImageSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [uploadSlot, setUploadSlot] = useState('extra');
  const [uploading, setUploading] = useState(false);
  const [showCertEdit, setShowCertEdit] = useState(false);
  const [certEditTitle, setCertEditTitle] = useState('');
  const [fullscreenIdx, setFullscreenIdx] = useState(null);
  const [processing, setProcessing] = useState(false);
  // Refund amount ($ input — blank = full refund. Converted to cents on submit.)
  const [refundAmount, setRefundAmount] = useState('');
  // Metadata editing (always-on form)
  const [metaForm, setMetaForm] = useState({});
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  // Appraisal authoring form state
  const [showAppraisalForm, setShowAppraisalForm] = useState(false);
  const [apprForm, setApprForm] = useState({
    appraised_value_cents: '',
    intended_use: '', condition: '', market_analysis: '',
    appraiser_signature: 'The Fashion Detective',
    disclaimer: 'This appraisal represents the appraiser\'s expert opinion of fair market value at the date of appraisal. Values may fluctuate based on market conditions.',
    references: '',
  });

  const loadItem = useCallback(async () => {
    try {
      const res = await api.get(`/team/items/${itemId}`);
      setItem(res.data);
    } catch (err) {
      toast.error('Failed to load item');
    } finally { setLoading(false); }
  }, [itemId]);

  useEffect(() => { loadItem(); }, [loadItem]);

  // Load categories/brands for metadata editing
  useEffect(() => {
    api.get('/catalog/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/catalog/brands').then(r => setBrands(r.data)).catch(() => {});
  }, []);

  // Load the category's photo slots for the request checklist + upload picker
  useEffect(() => {
    if (item?.category_slug) {
      api.get(`/catalog/image-slots/${item.category_slug}`)
        .then(r => setImageSlots(r.data.slots || []))
        .catch(() => setImageSlots([]));
    }
  }, [item?.category_slug]);

  // Pre-fill metadata form
  useEffect(() => {
    if (item) {
      setMetaForm({
        item_title: item.item_title || '',
        serial_number: item.serial_number || '',
        brand_slug: item.brand_slug || '',
        category_slug: item.category_slug || '',
      });
    }
  }, [item]);

  // Pre-fill appraisal form from intake data
  useEffect(() => {
    if (item?.appraisal_intake) {
      const intake = item.appraisal_intake;
      setApprForm(prev => ({
        ...prev,
        intended_use: prev.intended_use || intake.purpose || '',
        condition: prev.condition || intake.condition || '',
      }));
    }
    if (item?.appraisal_report) {
      const rpt = item.appraisal_report;
      setApprForm({
        appraised_value_cents: rpt.appraised_value_cents || '',
        intended_use: rpt.intended_use || '', condition: rpt.condition || '',
        market_analysis: rpt.market_analysis || '',
        appraiser_signature: rpt.appraiser_signature || 'The Fashion Detective',
        disclaimer: rpt.disclaimer || '', references: rpt.references || '',
      });
    }
  }, [item]);

  const doAction = async (fn) => {
    setProcessing(true);
    try { await fn(); await loadItem(); } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setProcessing(false); }
  };

  const setStatus = (status, notes) => doAction(() => api.put(`/team/items/${itemId}/status`, { status, notes }));
  const setDetermination = (det, notes) => doAction(() => api.put(`/team/items/${itemId}/determination`, { determination: det, notes }));
  const addNote = () => { if (!noteText.trim()) return; doAction(async () => { await api.post(`/team/items/${itemId}/notes`, { body: noteText }); setNoteText(''); }); };
  const requestImages = () => doAction(async () => {
    const fd = new FormData();
    fd.append('message', imageMsg);
    fd.append('requested_slots', JSON.stringify(selectedSlots));
    if (requestAttachment) fd.append('attachment', requestAttachment);
    await api.post(`/team/items/${itemId}/request-images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setImageMsg(''); setSelectedSlots([]); setRequestAttachment(null);
    toast.success('Image request sent to customer');
  });
  const toggleSlot = (slotKey) => setSelectedSlots(prev =>
    prev.includes(slotKey) ? prev.filter(k => k !== slotKey) : [...prev, slotKey]
  );
  const uploadEvidence = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('slot_key', uploadSlot);
        await api.post(`/team/items/${itemId}/upload-image`, fd);
      }
      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded`);
      await loadItem();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const rotateAsset = (assetId, deg) => doAction(() => api.put(`/team/items/${itemId}/assets/${assetId}/rotation`, { rotation_degrees: deg }));
  const toggleCertImage = (assetId, val) => {
    if (val) {
      const currentlySelected = (item?.assets || []).filter(a => a.is_certificate_image).length;
      if (currentlySelected >= 4) {
        toast.error('Only 4 images can appear on the certificate. Deselect one first.');
        return;
      }
    }
    return doAction(() => api.put(`/team/items/${itemId}/assets/${assetId}/certificate-select`, { is_certificate_image: val }));
  };
  const deleteAsset = (assetId) => {
    if (!confirm('Delete this image? This cannot be undone.')) return;
    doAction(async () => {
      await api.delete(`/team/items/${itemId}/assets/${assetId}`);
      toast.success('Image deleted');
    });
  };
  const revokeCertificate = () => {
    if (!confirm('Revoke this certificate WITHOUT refunding? The item returns to review and must be re-marked Authentic or Counterfeit.')) return;
    doAction(async () => {
      const r = await api.post(`/team/items/${itemId}/revoke-certificate`, {});
      toast.success(`Certificate ${r.data.revoked_certificate_code || ''} revoked — item returned to review`);
    });
  };
  const resetDetermination = () => {
    if (!confirm('Reset this Counterfeit determination? The item returns to In Review so it can be re-evaluated and a new determination issued.')) return;
    doAction(async () => {
      await api.post(`/team/items/${itemId}/reset-determination`, {});
      toast.success('Determination reset — item returned to In Review');
    });
  };
  const updateMailedDoc = (status) => doAction(() => api.put(`/team/items/${itemId}/mailed-doc`, { status }));
  const initiateRefund = (reason, revokesCert) => {
    // Blank input = full refund (backend treats null amount_cents as full).
    const raw = (refundAmount || '').trim();
    let amount_cents = null;
    if (raw) {
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        toast.error('Refund amount must be a positive number');
        return;
      }
      amount_cents = Math.round(parsed * 100);
    }
    doAction(async () => {
      const payload = { reason, revokes_certificate: revokesCert || false };
      if (amount_cents !== null) payload.amount_cents = amount_cents;
      await api.post(`/team/items/${itemId}/refund`, payload);
      setRefundAmount('');
    });
  };
  const generateCertificate = () => doAction(async () => {
    const payload = { order_item_id: itemId };
    if (customCertCode.trim()) payload.certificate_code = customCertCode.trim();
    const res = await api.post('/documents/certificates/generate', payload);
    toast.success(`Certificate generated: ${res.data.certificate_code}`);
    setCustomCertCode('');
  });
  const generateAppraisal = (formData) => doAction(async () => {
    await api.post('/documents/appraisals/generate', { order_item_id: itemId, ...formData });
    toast.success('Appraisal report generated');
  });
  const generateCounterfeitLetter = () => doAction(async () => {
    await api.post('/documents/counterfeit-letters/generate', { order_item_id: itemId });
    toast.success('Counterfeit letter generated');
  });
  const viewDocumentPdf = async (path) => {
    try {
      const res = await api.get(path);
      window.open(res.data.download_url, '_blank', 'noopener');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open PDF'); }
  };
  const regenerateCertificate = () => doAction(async () => {
    await api.post(`/documents/certificates/${item.certificate.id}/regenerate`, { display_title: certEditTitle });
    setShowCertEdit(false);
    toast.success('Certificate updated & regenerated');
  });
  const regenerateCounterfeitLetter = () => doAction(async () => {
    await api.post(`/documents/counterfeit-letters/${item.counterfeit_letter.id}/regenerate`);
    toast.success('Counterfeit letter regenerated');
  });

  const saveMetadata = () => doAction(async () => {
    const r = await api.put(`/team/items/${itemId}/metadata`, metaForm);
    toast.success(`Updated: ${r.data.changes?.join(', ') || 'saved'}`);
  });

  const saveTitle = () => doAction(async () => {
    await api.put(`/team/items/${itemId}/metadata`, { item_title: titleValue });
    toast.success('Item title updated');
    setEditingTitle(false);
  });

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!item) return <div className="text-center py-12"><p className="text-sm text-muted-foreground">Item not found</p></div>;

  const assets = item.assets || [];
  const notes = item.notes || [];
  const history = item.status_history || [];
  const mailedDocs = item.mailed_documents || [];
  const isTerminal = ['authentic','counterfeit','appraisal_complete','unable_to_determine','refunded','revoked'].includes(item.status);

  return (
    <div data-testid="team-item-detail">
      {/* Back + header */}
      <div className="flex items-center gap-3 mb-4">
        <Link to="/team" className="no-underline"><Button variant="ghost" size="sm" className="gap-1 text-xs h-7"><ArrowLeft className="h-3.5 w-3.5" /> Queue</Button></Link>
        <Separator orientation="vertical" className="h-5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {editingTitle ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input value={titleValue} onChange={e => setTitleValue(e.target.value)} autoFocus
                  className="flex-1 min-w-0 p-1.5 rounded border border-stone text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-ring/30"
                  data-testid="item-title-input"
                  onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }} />
                <Button size="sm" className="h-7 text-[10px] gap-1" onClick={saveTitle} disabled={processing} data-testid="item-title-save"><Save className="h-3 w-3" /> Save</Button>
                <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setEditingTitle(false)}>Cancel</Button>
              </div>
            ) : (
              <>
                <h1 className="text-base font-semibold text-ink truncate">{item.item_title || 'Untitled'}</h1>
                {!isTerminal && (
                  <button className="text-muted-foreground hover:text-ink" title="Edit item title"
                    onClick={() => { setTitleValue(item.item_title || ''); setEditingTitle(true); }}
                    data-testid="item-title-edit-btn">
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </>
            )}
            {item.is_expedited && <Badge className="text-[9px] h-4" style={{ background: 'rgba(163,106,58,0.12)', color: '#A36A3A' }}><Zap className="h-2.5 w-2.5 mr-0.5" />Expedited</Badge>}
            <Badge variant="outline" className="text-[10px]">{STATUS_LABELS[item.status] || item.status}</Badge>
            {item.refunded_cents > 0 && (
              <Badge
                className="text-[9px] h-4 gap-0.5"
                style={{ background: 'rgba(217,119,6,0.12)', color: '#B45309' }}
                title={`A refund of $${(item.refunded_cents / 100).toFixed(2)} was processed for this item.`}
                data-testid="refunded-badge"
              >
                <RefreshCw className="h-2.5 w-2.5" />
                Refunded ${(item.refunded_cents / 100).toFixed(2)}
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {item.brand_name} - {item.category_name} - {item.service_name} - <span className="font-mono">{item.order_number}</span>
            {item.customer_email && <> - {item.customer_email}</>}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* Left: Images + Details */}
        <div className="space-y-4">
          {/* Evidence Images */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Camera className="h-3.5 w-3.5" /> Evidence Images ({assets.length})
                  {(() => {
                    const selectedCount = assets.filter(a => a.is_certificate_image).length;
                    return (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-amber-700"
                        data-testid="cert-selected-count"
                      >
                        <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                        {selectedCount} of 4 on certificate
                      </span>
                    );
                  })()}
                </h3>
                {!isTerminal && (
                  <div className="flex items-center gap-2">
                    <select value={uploadSlot} onChange={e => setUploadSlot(e.target.value)} className="p-1.5 rounded border border-stone text-xs" data-testid="upload-slot-select">
                      <option value="extra">Extra / other</option>
                      {imageSlots.map(s => <option key={s.slot_key} value={s.slot_key}>{s.slot_label}</option>)}
                    </select>
                    <input id="staff-evidence-file" type="file" accept={IMAGE_UPLOAD_ACCEPT} multiple className="hidden" onChange={uploadEvidence} data-testid="upload-evidence-input" />
                    <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={() => document.getElementById('staff-evidence-file').click()} disabled={uploading} data-testid="upload-evidence-btn">
                      {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload evidence
                    </Button>
                  </div>
                )}
              </div>
              {assets.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No images uploaded</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {assets.map((asset, aIdx) => {
                    // Aug 2026: HEIC assets aren't browser-renderable — route
                    // through the transcoder endpoint. Non-HEIC uses the
                    // signed S3 URL directly (fast path).
                    const _isHeicAsset = (a) => {
                      const k = (a?.storage_key || '').toLowerCase();
                      const ct = (a?.content_type || '').toLowerCase();
                      return k.endsWith('.heic') || k.endsWith('.heif') || ct.includes('heic') || ct.includes('heif');
                    };
                    const url = _isHeicAsset(asset)
                      ? `${API_BASE}/team/items/${asset.order_item_id || itemId}/assets/${asset.id}/render.jpg`
                      : (asset.url || `/api/storage/files/${asset.storage_key}`);
                    const rotation = asset.rotation_degrees || 0;
                    return (
                      <div
                        key={asset.id}
                        className={`relative group rounded-lg overflow-hidden border bg-stone/5 transition-shadow ${
                          asset.is_certificate_image
                            ? 'border-amber-400 ring-2 ring-amber-300/60'
                            : 'border-stone/30'
                        }`}
                      >
                        <div className="aspect-square overflow-hidden">
                          <img
                            src={url} alt={asset.slot_key || 'image'}
                            className="w-full h-full object-cover cursor-pointer"
                            style={{ transform: `rotate(${rotation}deg)` }}
                            onClick={() => setFullscreenIdx(aIdx)}
                            data-testid={`asset-${asset.id}`}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        {/* Controls overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-1 bg-black/50 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                          <div className="flex gap-0.5">
                            <button onClick={() => rotateAsset(asset.id, (rotation - 90 + 360) % 360)} className="h-6 w-6 rounded flex items-center justify-center text-white hover:bg-white/20" title="Rotate left">
                              <RotateCcw className="h-3 w-3" />
                            </button>
                            <button onClick={() => rotateAsset(asset.id, (rotation + 90) % 360)} className="h-6 w-6 rounded flex items-center justify-center text-white hover:bg-white/20" title="Rotate right">
                              <RotateCw className="h-3 w-3" />
                            </button>
                            <button onClick={() => setFullscreenIdx(aIdx)} className="h-6 w-6 rounded flex items-center justify-center text-white hover:bg-white/20" title="Fullscreen">
                              <Maximize2 className="h-3 w-3" />
                            </button>
                            {!_isHeicAsset(asset) && asset.url && (
                              // HEIC assets: hide the Lens button. Its URL
                              // would either point at our auth-gated
                              // /render.jpg endpoint (Google's crawler has
                              // no session cookie) or at the raw HEIC S3
                              // URL (Lens can't decode HEIC either). Non-HEIC
                              // assets keep the direct S3 signed URL, which
                              // Lens fetches happily.
                              <a
                                href={`https://lens.google.com/uploadbyurl?url=${encodeURIComponent(asset.url)}`}
                                target="_blank" rel="noreferrer"
                                className="h-6 w-6 rounded flex items-center justify-center text-white hover:bg-white/20"
                                title="Search image with Google Lens"
                                data-testid={`lens-${asset.id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ScanSearch className="h-3 w-3" />
                              </a>
                            )}
                            <button onClick={() => deleteAsset(asset.id)} className="h-6 w-6 rounded flex items-center justify-center text-red-300 hover:bg-white/20" title="Delete image" data-testid={`delete-asset-${asset.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <span className="absolute top-1 left-1 text-[9px] bg-black/50 text-white px-1 rounded">{asset.slot_key}{asset.is_extra ? ' (extra)' : ''}</span>
                        {/* Always-visible cert-select star (Aug 2026):
                            tap to toggle whether this image appears on
                            the certificate PDF. Filled yellow = on cert;
                            outline white = available. Hover tooltip
                            explains both states; the parent card also
                            carries an amber ring when selected so the
                            state is legible without hovering. */}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleCertImage(asset.id, !asset.is_certificate_image); }}
                          className={`absolute top-1 right-1 h-6 w-6 rounded-full flex items-center justify-center transition-colors ${
                            asset.is_certificate_image
                              ? 'bg-amber-500 text-white shadow-md shadow-amber-500/40 hover:bg-amber-600'
                              : 'bg-black/55 text-white/85 hover:bg-black/70'
                          }`}
                          title={asset.is_certificate_image ? 'On certificate — tap to remove' : 'Tap to add to certificate'}
                          data-testid={`cert-star-${asset.id}`}
                          disabled={processing}
                        >
                          <Star className={`h-3.5 w-3.5 ${asset.is_certificate_image ? 'fill-current' : ''}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Item Metadata — always editable */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Item Details</h3>
                <Button size="sm" className="h-6 text-[10px] gap-0.5" onClick={saveMetadata} disabled={processing} data-testid="update-meta-btn">
                  <Save className="h-2.5 w-2.5" /> Update
                </Button>
              </div>
              <div className="space-y-2 text-xs">
                <div><label className="text-muted-foreground text-[10px]">Item Title</label><input value={metaForm.item_title || ''} onChange={e => setMetaForm({...metaForm, item_title: e.target.value})} className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs focus:outline-none focus:ring-1 focus:ring-ring/30" data-testid="meta-title-input" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-muted-foreground text-[10px]">Serial Number</label><input value={metaForm.serial_number || ''} onChange={e => setMetaForm({...metaForm, serial_number: e.target.value})} className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs" data-testid="meta-serial-input" /></div>
                  <div><label className="text-muted-foreground text-[10px]">Category</label>
                    <select value={metaForm.category_slug || ''} onChange={e => setMetaForm({...metaForm, category_slug: e.target.value})} className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs" data-testid="meta-category-select">
                      {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div><label className="text-muted-foreground text-[10px]">Brand</label>
                  <select value={metaForm.brand_slug || ''} onChange={e => setMetaForm({...metaForm, brand_slug: e.target.value})} className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs" data-testid="meta-brand-select">
                    <option value="">No brand on record (leave unchanged)</option>
                    {brands.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                    <option value="other">Clear brand (Other / None)</option>
                  </select>
                </div>
              </div>
              <Separator className="my-3" />
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground text-xs">Service</span><p className="font-medium">{item.service_name}</p></div>
                <div><span className="text-muted-foreground text-xs">Order</span><p className="text-xs font-mono">{item.order_number}</p></div>
                <div className="col-span-2"><span className="text-muted-foreground text-xs">Description</span><p className="text-xs">{item.description || '-'}</p></div>
                <div className="col-span-2"><span className="text-muted-foreground text-xs">Customer</span><p className="text-xs">{item.customer_email}</p></div>
                {item.appraisal_intake && (
                  <>
                    <div><span className="text-muted-foreground text-xs">Appraisal Purpose</span><p className="text-xs font-medium capitalize">{item.appraisal_intake.purpose || '-'}</p></div>
                    <div><span className="text-muted-foreground text-xs">Stated Condition</span><p className="text-xs font-medium capitalize">{(item.appraisal_intake.condition || '-').replace('_', ' ')}</p></div>
                    {item.appraisal_intake.provenance && (
                      <div className="col-span-2"><span className="text-muted-foreground text-xs">Provenance</span><p className="text-xs">{item.appraisal_intake.provenance}</p></div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Customer History */}
          <Card className="bg-white border-stone/50" data-testid="customer-history-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> Customer History</h3>
                {item.customer_id && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => navigate(`/team/users/${item.customer_id}`)} data-testid="view-order-history-btn">
                    <ShoppingBag className="h-2.5 w-2.5" /> View Order History
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-lg bg-stone/5 border border-stone/20">
                  <p className="text-lg font-semibold text-ink" data-testid="cust-total-orders">{item.customer_stats?.total_orders ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground">Total Orders</p>
                </div>
                <div className="p-2 rounded-lg border" style={{ background: 'rgba(14,107,93,0.06)', borderColor: 'rgba(14,107,93,0.2)' }}>
                  <p className="text-lg font-semibold" style={{ color: '#0E6B5D' }} data-testid="cust-authentic-count">{item.customer_stats?.authentic_count ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground">Authentic</p>
                </div>
                <div className="p-2 rounded-lg border" style={{ background: 'rgba(90,31,42,0.06)', borderColor: 'rgba(90,31,42,0.2)' }}>
                  <p className="text-lg font-semibold" style={{ color: '#5A1F2A' }} data-testid="cust-counterfeit-count">{item.customer_stats?.counterfeit_count ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground">Counterfeit</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Order Billing */}
          {item.order_billing && <OrderBillingCard billing={item.order_billing} />}
        </div>

        {/* Right: Actions panel */}
        <div className="space-y-4">
          {/* Determination Actions */}
          {!isTerminal && (
            <Card className="bg-white border-stone/50">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Actions</h3>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Set Determination:</p>
                  {/* Guard (Aug 2026): block Authentic / Counterfeit /
                      Appraisal Complete when no evidence image exists —
                      those paths stamp a cert, letter, or report that
                      has nothing to show. Unable to Determine is the
                      one allowed path (it exists precisely for the
                      no-usable-images case). */}
                  {(() => {
                    const hasAssets = (item.assets || []).length > 0;
                    const needImagesMsg = !hasAssets ? (
                      <div
                        className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] leading-tight text-amber-800"
                        data-testid="determination-needs-image-warning"
                      >
                        <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>
                          Upload at least one evidence image before saving a determination.
                          You can still choose "Unable to Determine" when no images are available.
                        </span>
                      </div>
                    ) : null;
                    return (
                      <>
                        {needImagesMsg}
                        {item.service_family === 'authentication' ? (
                          <>
                            <CertificatePreviewButton itemId={itemId} />
                            <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 justify-start" style={{ color: '#0E6B5D' }} onClick={() => setDetermination('authentic')} disabled={processing || !hasAssets} data-testid="det-authentic">
                              <CheckCircle className="h-3.5 w-3.5" /> Authentic
                            </Button>
                            <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 justify-start" style={{ color: '#5A1F2A' }} onClick={() => setDetermination('counterfeit')} disabled={processing || !hasAssets} data-testid="det-counterfeit">
                              <XCircle className="h-3.5 w-3.5" /> Counterfeit
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 justify-start" style={{ color: '#0E6B5D' }} onClick={() => setDetermination('appraisal_complete')} disabled={processing || !hasAssets} data-testid="det-appraisal">
                            <CheckCircle className="h-3.5 w-3.5" /> Appraisal Complete
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 justify-start text-muted-foreground" onClick={() => setDetermination('unable_to_determine')} disabled={processing} data-testid="det-unable">
                          <HelpCircle className="h-3.5 w-3.5" /> Unable to Determine (auto-refund)
                        </Button>
                      </>
                    );
                  })()}
                </div>
                <Separator className="my-3" />
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Request More Images:</p>
                  {imageSlots.length > 0 && (
                    <div className="space-y-1 p-2 rounded-lg border border-stone/30 bg-stone/5" data-testid="request-slots-checklist">
                      {imageSlots.map(s => {
                        const uploaded = assets.some(a => a.slot_key === s.slot_key);
                        const checked = selectedSlots.includes(s.slot_key);
                        return (
                          <label key={s.slot_key} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`request-slot-${s.slot_key}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleSlot(s.slot_key)} className="accent-current" />
                            <span className="flex-1">{s.slot_label}</span>
                            {uploaded ? (
                              <span className="text-[10px] flex items-center gap-0.5" style={{ color: '#0E6B5D' }}><CheckCircle className="h-3 w-3" /> uploaded</span>
                            ) : (
                              <span className="text-[10px]" style={{ color: '#A36A3A' }}>missing</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <textarea
                    value={imageMsg}
                    onChange={e => setImageMsg(e.target.value)}
                    placeholder="Optional note to the customer (e.g. specific angle or detail needed)..."
                    className="w-full p-2 rounded border border-stone text-xs min-h-[50px] resize-y focus:outline-none focus:ring-1 focus:ring-ring/30"
                    data-testid="request-images-message"
                  />
                  <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer" data-testid="request-images-attachment-label">
                    <Upload className="h-3 w-3 shrink-0" />
                    <span className="truncate">{requestAttachment ? requestAttachment.name : 'Add an attachment (optional)'}</span>
                    <input type="file" className="hidden" data-testid="request-images-attachment"
                      onChange={e => setRequestAttachment(e.target.files?.[0] || null)} />
                    {requestAttachment && (
                      <button type="button" className="text-red-400 text-[10px]" onClick={(e) => { e.preventDefault(); setRequestAttachment(null); }}>remove</button>
                    )}
                  </label>
                  <Button size="sm" variant="outline" className="w-full text-xs gap-1.5" onClick={requestImages} disabled={processing || (selectedSlots.length === 0 && !imageMsg.trim())} data-testid="request-images-btn">
                    <MessageSquare className="h-3.5 w-3.5" /> Request More Images{selectedSlots.length > 0 ? ` (${selectedSlots.length} selected)` : ''}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Email customer */}
          <EmailCustomerCard itemId={itemId} />

          {/* Document Generation */}
          {(item.certificate || item.counterfeit_letter || item.appraisal_report || item.status === 'authentic' || item.determination === 'authentic' || item.determination === 'appraisal_complete' || item.determination === 'counterfeit') && (
            <Card className="bg-white border-stone/50">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><FileCheck className="h-3 w-3" /> Documents</h3>
                {(item.determination === 'authentic' || item.status === 'authentic') && !item.certificate && (
                  <div className="mb-2 space-y-1.5">
                    <input value={customCertCode} onChange={e => setCustomCertCode(e.target.value)}
                      className="w-full p-1.5 rounded border border-stone text-xs focus:outline-none focus:ring-1 focus:ring-ring/30 font-mono"
                      placeholder="Original certificate code (optional, e.g. KHI9LZ4)" data-testid="custom-cert-code-input" />
                    <p className="text-[9px] text-muted-foreground">Leave blank to auto-generate a new code. Enter the code printed on an existing certificate to keep it verifiable.</p>
                    <Button size="sm" className="w-full text-xs gap-1.5" style={{ background: 'hsl(var(--accent-emerald))' }} onClick={generateCertificate} disabled={processing} data-testid="gen-cert-btn">
                      <Award className="h-3.5 w-3.5" /> Generate Certificate
                    </Button>
                  </div>
                )}
                {item.certificate && (
                  <div className="p-2 rounded-lg bg-emerald-50/50 border border-emerald-200 text-xs mb-2">
                    <div className="flex items-center gap-1 mb-1"><Award className="h-3 w-3" style={{ color: '#0E6B5D' }} /><span className="font-medium" style={{ color: '#0E6B5D' }}>Certificate Issued</span></div>
                    <span className="font-mono text-[10px]">{item.certificate.certificate_code}</span>
                    {item.certificate.is_revoked && <Badge className="ml-2 text-[9px]" style={{ background: 'rgba(90,31,42,0.12)', color: '#5A1F2A' }}>Revoked</Badge>}
                    <div className="flex gap-1.5 mt-2">
                      <Button size="sm" variant="outline" className="flex-1 text-[10px] h-7 gap-1" onClick={() => viewDocumentPdf(`/documents/certificates/${item.certificate.id}/pdf`)} data-testid="view-cert-pdf-btn">
                        <FileText className="h-3 w-3" /> View PDF
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 text-[10px] h-7 gap-1" onClick={() => { setCertEditTitle(item.certificate.display_title || item.item_title || ''); setShowCertEdit(!showCertEdit); }} data-testid="edit-cert-btn">
                        <RefreshCw className="h-3 w-3" /> Edit &amp; Regenerate
                      </Button>
                    </div>
                    {showCertEdit && (
                      <div className="mt-2 p-2 rounded bg-white/70 space-y-1.5">
                        <label className="text-[10px] text-muted-foreground">Item name shown on certificate</label>
                        <input value={certEditTitle} onChange={(e) => setCertEditTitle(e.target.value)} className="w-full p-1.5 rounded border border-stone text-xs" data-testid="cert-edit-title-input" />
                        <p className="text-[9px] text-muted-foreground">Regenerates the PDF with this name and the currently selected certificate images (star photos above). Same certificate code.</p>
                        <Button size="sm" className="w-full text-[10px] h-7" onClick={regenerateCertificate} disabled={processing} data-testid="regen-cert-btn">Regenerate Certificate</Button>
                      </div>
                    )}
                  </div>
                )}
                {item.determination === 'appraisal_complete' && (
                  <>
                    <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 mb-2" onClick={() => setShowAppraisalForm(!showAppraisalForm)} data-testid="gen-appraisal-btn">
                      <FileText className="h-3.5 w-3.5" /> {item.appraisal_report ? 'Update Appraisal Report' : 'Create Appraisal Report'}
                    </Button>
                    {showAppraisalForm && (
                      <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/30 space-y-2 text-xs">
                        {/* Show customer intake data for reference */}
                        {item.appraisal_intake && (
                          <div className="p-2 rounded bg-white/60 mb-2">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Customer Intake</p>
                            {item.appraisal_intake.purpose && <p><span className="text-muted-foreground">Purpose:</span> {item.appraisal_intake.purpose}</p>}
                            {item.appraisal_intake.condition && <p><span className="text-muted-foreground">Condition:</span> {item.appraisal_intake.condition}</p>}
                            {item.appraisal_intake.provenance && <p><span className="text-muted-foreground">Provenance:</span> {item.appraisal_intake.provenance}</p>}
                          </div>
                        )}
                        <div><label className="text-[10px] text-muted-foreground">Appraised Value ($)</label>
                          <input type="number" value={apprForm.appraised_value_cents ? apprForm.appraised_value_cents / 100 : ''} onChange={e => setApprForm({...apprForm, appraised_value_cents: Math.round((parseFloat(e.target.value) || 0) * 100)})} placeholder="0.00" className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs" data-testid="appr-value" /></div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><label className="text-[10px] text-muted-foreground">Intended Use</label>
                            <select value={apprForm.intended_use} onChange={e => setApprForm({...apprForm, intended_use: e.target.value})} className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs">
                              <option value="">Select</option><option value="insurance">Insurance</option><option value="estate">Estate</option><option value="donation">Donation</option><option value="resale">Resale</option><option value="personal">Personal Knowledge</option>
                            </select></div>
                          <div><label className="text-[10px] text-muted-foreground">Condition</label>
                            <select value={apprForm.condition} onChange={e => setApprForm({...apprForm, condition: e.target.value})} className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs">
                              <option value="">Select</option><option value="excellent">Excellent</option><option value="very_good">Very Good</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option>
                            </select></div>
                        </div>
                        <div><label className="text-[10px] text-muted-foreground">Market Analysis</label>
                          <textarea value={apprForm.market_analysis} onChange={e => setApprForm({...apprForm, market_analysis: e.target.value})} placeholder="Comparable sales, market trends..." className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs min-h-[50px] resize-y" /></div>
                        <div><label className="text-[10px] text-muted-foreground">References</label>
                          <input value={apprForm.references} onChange={e => setApprForm({...apprForm, references: e.target.value})} placeholder="Reference sources" className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs" /></div>
                        <div><label className="text-[10px] text-muted-foreground">Appraiser Signature</label>
                          <input value={apprForm.appraiser_signature} onChange={e => setApprForm({...apprForm, appraiser_signature: e.target.value})} className="w-full mt-0.5 p-1.5 rounded border border-stone text-xs" /></div>
                        <Button size="sm" className="w-full text-xs gap-1" onClick={() => { if (!apprForm.appraised_value_cents) { toast.error('Value required'); return; } generateAppraisal(apprForm); setShowAppraisalForm(false); }} disabled={processing} data-testid="submit-appraisal-btn">
                          <FileText className="h-3 w-3" /> {item.appraisal_report ? 'Regenerate Report' : 'Generate Report'}
                        </Button>
                      </div>
                    )}
                    {item.appraisal_report && !showAppraisalForm && (
                      <div className="p-2 rounded-lg bg-amber-50/50 border border-amber-200 text-xs">
                        <p className="font-medium" style={{ color: '#A36A3A' }}>Report exists — ${(item.appraisal_report.appraised_value_cents / 100).toLocaleString()}</p>
                        <p className="text-[10px] text-muted-foreground">{item.appraisal_report.intended_use} / {item.appraisal_report.condition}</p>
                      </div>
                    )}
                  </>
                )}
                {item.determination === 'counterfeit' && !item.counterfeit_letter && (
                  <Button size="sm" variant="outline" className="w-full text-xs gap-1.5" onClick={generateCounterfeitLetter} disabled={processing} data-testid="gen-counterfeit-btn">
                    <AlertTriangle className="h-3.5 w-3.5" /> Generate Counterfeit Letter
                  </Button>
                )}
                {item.counterfeit_letter && (
                  <div className="p-2 rounded-lg bg-red-50/50 border border-red-200 text-xs" data-testid="counterfeit-letter-box">
                    <div className="flex items-center gap-1 mb-1"><AlertTriangle className="h-3 w-3 text-red-700" /><span className="font-medium text-red-700">Counterfeit Letter Issued</span></div>
                    <div className="flex gap-1.5 mt-2">
                      <Button size="sm" variant="outline" className="flex-1 text-[10px] h-7 gap-1" onClick={() => viewDocumentPdf(`/documents/counterfeit-letters/${item.counterfeit_letter.id}/pdf`)} data-testid="view-counterfeit-pdf-btn">
                        <FileText className="h-3 w-3" /> View PDF
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 text-[10px] h-7 gap-1" onClick={regenerateCounterfeitLetter} disabled={processing} data-testid="regen-counterfeit-btn">
                        <RefreshCw className="h-3 w-3" /> Regenerate
                      </Button>
                    </div>
                  </div>
                )}

                {/* Letter History — shows every superseded (invalidated)
                    counterfeit letter so admins can open an old PDF
                    alongside the active one and verify what changed
                    after a Reset-Determination. Only renders when at
                    least one invalidated letter exists for this item. */}
                <CounterfeitLetterHistory
                  history={item.counterfeit_letter_history}
                  onView={viewDocumentPdf}
                />
              </CardContent>
            </Card>
          )}

          {/* Revoke certificate without refund */}
          {isTerminal && item.status !== 'refunded' && item.certificate && !item.certificate.is_revoked && (
            <Card className="bg-white border-stone/50" data-testid="revoke-cert-card">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><XCircle className="h-3 w-3" /> Certificate Revocation</h3>
                <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 text-red-700" onClick={revokeCertificate} disabled={processing} data-testid="revoke-cert-btn">
                  <XCircle className="h-3.5 w-3.5" /> Revoke Certificate (No Refund)
                </Button>
                <p className="text-[10px] text-muted-foreground mt-1.5">Marks the certificate revoked and returns the item to review. You must then re-mark it Authentic or Counterfeit — marking Authentic issues a NEW certificate code.</p>
              </CardContent>
            </Card>
          )}

          {/* Reset Determination — counterfeit undo. Full Admin only.
              Mirrors the Revoke-Certificate card above but for the Counterfeit
              side of a terminal case: clears the verdict, invalidates any
              generated counterfeit letter, and returns the item to In Review
              so staff can re-evaluate. */}
          {isFullAdmin && (item.status === 'counterfeit' || item.determination === 'counterfeit') && (
            <Card className="bg-white border-stone/50" data-testid="reset-determination-card">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Reset Determination</h3>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs gap-1.5 text-amber-700"
                  onClick={resetDetermination}
                  disabled={processing}
                  data-testid="reset-determination-btn"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Reset Determination
                </Button>
                <p className="text-[10px] text-muted-foreground mt-1.5">Clears the Counterfeit verdict and returns the item to In Review. Any counterfeit letter that was generated is invalidated but kept in the audit trail. Full Admin only.</p>
              </CardContent>
            </Card>
          )}

          {/* Refund */}
          {!['refunded','revoked'].includes(item.status) && (
            <Card className="bg-white border-stone/50">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Refund</h3>
                <div className="mb-2.5">
                  <Label htmlFor="refund-amount" className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Amount (USD) — leave blank for full
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">$</span>
                    <Input
                      id="refund-amount"
                      data-testid="refund-amount-input"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      placeholder={((item.total_cents || 0) / 100).toFixed(2)}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      className="h-8 pl-5 text-xs"
                      disabled={processing}
                    />
                  </div>
                </div>
                <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 text-red-600" onClick={() => initiateRefund('Staff-initiated refund')} disabled={processing} data-testid="refund-btn">
                  <RefreshCw className="h-3.5 w-3.5" /> Initiate Refund
                </Button>
                {item.certificate && (
                  <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 text-red-700 mt-2" onClick={() => initiateRefund('Refund + revoke certificate', true)} disabled={processing} data-testid="refund-revoke-btn">
                    <XCircle className="h-3.5 w-3.5" /> Refund & Revoke Certificate
                  </Button>
                )}
                <p className="text-[10px] text-muted-foreground mt-1.5">Refunds the money and bumps the order's payment status — the item stays open in its current determination. Only an "Unable to Determine" verdict both refunds and closes the item.</p>
              </CardContent>
            </Card>
          )}

          {/* Mailed Document */}
          {item.add_ons?.some(a => a.add_on_type === 'mailed_document') && (
            <Card className="bg-white border-stone/50">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Mail className="h-3 w-3" /> Mailed Document</h3>
                {/* Shipping address */}
                {item.shipping_address && (
                  <div className="p-2 rounded-lg bg-stone/5 border border-stone/20 mb-3 text-xs">
                    <p className="text-muted-foreground text-[10px] mb-1">Ship to:</p>
                    <p className="text-ink">
                      {item.shipping_address.line_1}{item.shipping_address.line_2 ? `, ${item.shipping_address.line_2}` : ''}<br />
                      {item.shipping_address.city}, {item.shipping_address.state} {item.shipping_address.postal_code}
                    </p>
                  </div>
                )}
                {mailedDocs.length > 0 ? (
                  <div className="space-y-2">
                    {mailedDocs.map(md => (
                      <div key={md.id} className="text-xs flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] ${md.status === 'shipped' ? 'bg-emerald-50 text-emerald-700' : md.status === 'cancelled' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'}`}>{md.status}</Badge>
                        {md.shipped_at && <span className="text-muted-foreground">Shipped: {new Date(md.shipped_at).toLocaleDateString()}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mb-2">No fulfillment record yet.</p>
                )}
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" className="text-xs gap-1 flex-1" onClick={() => updateMailedDoc('ready_to_print')} disabled={processing}>
                    <Printer className="h-3 w-3" /> Ready
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs gap-1 flex-1" onClick={() => updateMailedDoc('shipped')} disabled={processing}>
                    <Truck className="h-3 w-3" /> Shipped
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Notes ({notes.length})</h3>
              <div className="flex gap-2 mb-3">
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 p-2 rounded border border-stone text-xs min-h-[60px] resize-y focus:outline-none focus:ring-1 focus:ring-ring/30"
                  data-testid="note-input"
                />
              </div>
              <Button size="sm" className="w-full text-xs" onClick={addNote} disabled={processing || !noteText.trim()} data-testid="add-note-btn">Add Note</Button>
              {notes.length > 0 && (
                <div className="mt-3 space-y-2 max-h-[200px] overflow-y-auto">
                  {notes.map(n => (
                    <div key={n.id} className="p-2 rounded bg-stone/10 text-xs">
                      <p className="text-ink">{n.body}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{n.author_email} - {n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status History */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Clock className="h-3 w-3" /> Status History</h3>
              <div className="space-y-2">
                {history.map((evt, idx) => {
                  // Highlight counterfeit-reset events (from_status counterfeit
                  // → to_status in_review) so admins can trace disputes at a
                  // glance. The reason & actor are already carried in notes +
                  // actor_email by the backend.
                  const isReset = evt.from_status === 'counterfeit' && evt.to_status === 'in_review';
                  return (
                    <div
                      key={idx}
                      className={`flex items-start gap-2 text-xs ${isReset ? 'p-2 rounded border border-amber-200 bg-amber-50/40' : ''}`}
                      data-testid={isReset ? `reset-event-${idx}` : undefined}
                    >
                      {isReset
                        ? <RefreshCw className="h-3 w-3 text-amber-700 mt-0.5 flex-shrink-0" />
                        : <div className="w-1.5 h-1.5 rounded-full bg-ink mt-1.5 flex-shrink-0" />
                      }
                      <div>
                        {isReset && (
                          <Badge variant="outline" className="mr-1 h-4 text-[9px] border-amber-400 text-amber-800 bg-amber-50">
                            Counterfeit Reset
                          </Badge>
                        )}
                        <span className="font-medium">{STATUS_LABELS[evt.to_status] || evt.to_status}</span>
                        {evt.notes && <span className="text-muted-foreground"> - {evt.notes}</span>}
                        {(evt.metadata?.attachments || []).filter(a => a.url).map((att, i) => (
                          <a key={i} href={att.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] underline ml-1" style={{ color: '#1F5E7A' }} data-testid="history-attachment-link">
                            <Paperclip className="h-2.5 w-2.5" />{att.filename}
                          </a>
                        ))}
                        <p className="text-[10px] text-muted-foreground">
                          {evt.created_at ? new Date(evt.created_at).toLocaleString() : ''}
                          {evt.actor_email && ` by ${evt.actor_email}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Certificate History (Feb 2026) — every issue/regen/revoke event
              recorded in status_events, filtered to certificate-related notes. */}
          <CertificateHistoryCard itemId={itemId} certificate={item.certificate} />
        </div>
      </div>

      {/* Image Inspector with zoom, pan, annotations, and carousel navigation */}
      {fullscreenIdx != null && assets[fullscreenIdx] && (
        <ImageInspector
          asset={{ ...assets[fullscreenIdx], order_item_id: assets[fullscreenIdx].order_item_id || itemId, url: assets[fullscreenIdx].url || `/api/storage/files/${assets[fullscreenIdx].storage_key}` }}
          position={`${fullscreenIdx + 1} / ${assets.length}`}
          onPrev={fullscreenIdx > 0 ? () => setFullscreenIdx(fullscreenIdx - 1) : null}
          onNext={fullscreenIdx < assets.length - 1 ? () => setFullscreenIdx(fullscreenIdx + 1) : null}
          onClose={() => setFullscreenIdx(null)}
          onRotate={(deg) => rotateAsset(assets[fullscreenIdx].id, deg)}
          onToggleCert={(val) => toggleCertImage(assets[fullscreenIdx].id, val)}
        />
      )}
    </div>
  );
}

// ─── Order Billing panel ──────────────────────────────────────────────────
const fmtMoney = (cents, currency = 'USD') => {
  if (cents == null || cents === '') return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

const providerLabel = (p) => {
  const key = (p || '').toLowerCase();
  if (key === 'stripe') return 'Stripe';
  if (key === 'paypal') return 'PayPal';
  if (key === 'mock') return 'Manual / $0';
  return p || '—';
};

function OrderBillingCard({ billing }) {
  const currency = billing.currency || 'USD';
  const refundTotal = (billing.refunds || []).reduce((sum, r) => sum + (r.amount_cents || 0), 0);
  const dateShort = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } catch { return iso; }
  };

  return (
    <Card className="bg-white border-stone/50" data-testid="order-billing-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Order Billing
          </h3>
          <span className="text-[10px] text-muted-foreground font-mono" data-testid="billing-order-number">
            {billing.order_number}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          <div className="text-muted-foreground">Order date</div>
          <div className="text-right">{dateShort(billing.created_at)}</div>
          {billing.paid_at && (
            <>
              <div className="text-muted-foreground">Paid</div>
              <div className="text-right">{dateShort(billing.paid_at)}</div>
            </>
          )}
          <div className="text-muted-foreground">Subtotal</div>
          <div className="text-right" data-testid="billing-subtotal">{fmtMoney(billing.subtotal_cents, currency)}</div>
          {billing.discount_cents > 0 && (
            <>
              <div className="text-muted-foreground">
                Discount{billing.coupon_code ? <span className="font-mono ml-1 text-ink">({billing.coupon_code})</span> : ''}
              </div>
              <div className="text-right text-[#0E6B5D]" data-testid="billing-discount">-{fmtMoney(billing.discount_cents, currency)}</div>
            </>
          )}
          <div className="text-muted-foreground font-semibold">Total</div>
          <div className="text-right font-semibold" data-testid="billing-total">{fmtMoney(billing.total_cents, currency)}</div>
          {billing.payment_charged_amount_cents != null && billing.payment_charged_amount_cents !== billing.total_cents && (
            <>
              <div className="text-muted-foreground">Charged</div>
              <div className="text-right" data-testid="billing-charged">{fmtMoney(billing.payment_charged_amount_cents, currency)}</div>
            </>
          )}
          {refundTotal > 0 && (
            <>
              <div className="text-muted-foreground">Refunded</div>
              <div className="text-right text-[#5A1F2A]" data-testid="billing-refunded">-{fmtMoney(refundTotal, currency)}</div>
            </>
          )}
        </div>

        <Separator className="my-3" />

        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          <div className="text-muted-foreground">Payment</div>
          <div className="text-right" data-testid="billing-provider">{providerLabel(billing.payment_provider)}</div>
          {billing.payment_status && (
            <>
              <div className="text-muted-foreground">Status</div>
              <div className="text-right capitalize" data-testid="billing-payment-status">{billing.payment_status.replace('_', ' ')}</div>
            </>
          )}
          {billing.payment_provider_id && (
            <>
              <div className="text-muted-foreground">Transaction</div>
              <div className="text-right text-[10px] font-mono break-all" data-testid="billing-provider-id">{billing.payment_provider_id}</div>
            </>
          )}
          {billing.payment_needs_review && (
            <div className="col-span-2 mt-1 text-[10px] rounded border border-amber-400/40 bg-amber-50 text-amber-800 px-2 py-1" data-testid="billing-review-flag">
              Payment held for review (amount mismatch)
            </div>
          )}
        </div>

        {(billing.refunds || []).length > 0 && (
          <>
            <Separator className="my-3" />
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Refunds</p>
            <div className="space-y-1" data-testid="billing-refunds-list">
              {billing.refunds.map((r) => (
                <div key={r.id} className="flex justify-between items-center text-[11px]">
                  <span className="text-muted-foreground">
                    {dateShort(r.processed_at || r.created_at)}
                    {r.refund_type ? <span className="ml-1 capitalize">({r.refund_type})</span> : ''}
                  </span>
                  <span className="font-mono">{fmtMoney(r.amount_cents, currency)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Certificate Preview (Feb 2026) ──────────────────────────────────────
function CertificatePreviewButton({ itemId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/items/${itemId}/certificate-preview-images`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v && !data) load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 justify-start" data-testid="cert-preview-btn">
          <FileText className="h-3.5 w-3.5" /> Preview COA Images
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="cert-preview-dialog">
        <DialogHeader><DialogTitle className="text-sm">Certificate photo preview</DialogTitle></DialogHeader>
        {loading || !data ? (
          <div className="py-8 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>
        ) : (
          <>
            <p className="text-[11px] text-muted-foreground">
              These are the 4 photos that will print on the certificate. {data.source_count} uploaded
              {data.source_count < 4 && data.source_count > 0 && ' — the layout cycles to fill 4 spots.'}
              {data.source_count === 0 && ' — no images uploaded yet, cert will render blank photo squares.'}
            </p>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {data.images.map((img, i) => (
                <div key={i} className="aspect-square rounded border border-stone/40 overflow-hidden relative bg-stone/5" data-testid={`cert-preview-img-${i}`}>
                  {img.url ? (
                    <img src={img.url} alt={img.slot_key} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">no image</div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 flex justify-between">
                    <span>{img.slot_key || '—'}</span>
                    {img.cycled && <span className="text-yellow-300">repeat</span>}
                  </div>
                </div>
              ))}
              {data.images.length === 0 && (
                <div className="col-span-4 py-8 text-center text-xs text-muted-foreground">
                  No item images uploaded. Cert will render 4 empty photo squares.
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Certificate History (Feb 2026) ──────────────────────────────────────
function CertificateHistoryCard({ itemId, certificate }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    if (!certificate) return;
    api.get(`/team/items/${itemId}/certificate-history`)
      .then(r => setEvents(r.data || []))
      .catch(() => setEvents([]));
  }, [itemId, certificate]);
  if (!certificate) return null;
  return (
    <Card className="bg-white border-stone/50" data-testid="cert-history-card">
      <CardContent className="p-4">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1">
          <FileText className="h-3 w-3" /> Certificate History
        </h3>
        {events === null ? (
          <div className="py-4"><Loader2 className="h-3 w-3 animate-spin mx-auto text-muted-foreground" /></div>
        ) : events.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No history yet.</p>
        ) : (
          <div className="space-y-2">
            {events.map((e, idx) => (
              <div key={e.id || idx} className="flex items-start gap-2 text-xs" data-testid={`cert-history-${idx}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-ink mt-1.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[11px] text-ink">{e.notes || '(no notes)'}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {e.created_at ? new Date(e.created_at).toLocaleString() : '—'}
                    {e.actor_email && ` · ${e.actor_email}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Counterfeit Letter History (Feb 2026) ───────────────────────────────
// Renders superseded (invalidated) counterfeit letters under the active one
// so admins can open an old PDF alongside the current one and verify what
// changed after a Reset-Determination. The active letter is already shown
// above by the main card; this component intentionally only lists the
// invalidated rows and stays hidden when there is nothing to compare.
function CounterfeitLetterHistory({ history, onView }) {
  const invalidated = (history || []).filter(l => l.is_invalidated);
  if (invalidated.length === 0) return null;
  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  return (
    <div className="mt-3 p-2 rounded-lg bg-stone/5 border border-stone/40 text-xs" data-testid="counterfeit-letter-history">
      <div className="flex items-center gap-1 mb-2 text-muted-foreground">
        <Clock className="h-3 w-3" />
        <span className="font-medium text-[11px]">Superseded Letters ({invalidated.length})</span>
      </div>
      <div className="space-y-2">
        {invalidated.map((ltr, idx) => (
          <div
            key={ltr.id}
            className="p-2 rounded border border-stone/40 bg-white"
            data-testid={`superseded-letter-${idx}`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Badge variant="outline" className="text-[9px] h-4 border-amber-400 text-amber-800 bg-amber-50">
                Invalidated
              </Badge>
              <span className="text-[10px] text-muted-foreground">
                Issued {fmt(ltr.created_at)}
                {ltr.issued_by_email && ` · by ${ltr.issued_by_email}`}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground">
              Reset {fmt(ltr.invalidated_at)}
              {ltr.invalidated_by_email && ` · by ${ltr.invalidated_by_email}`}
            </div>
            {ltr.invalidation_reason && (
              <div className="mt-1 text-[11px] text-ink italic" data-testid={`superseded-letter-reason-${idx}`}>
                "{ltr.invalidation_reason}"
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              className="w-full mt-2 text-[10px] h-7 gap-1"
              onClick={() => onView(`/documents/counterfeit-letters/${ltr.id}/pdf`)}
              data-testid={`view-superseded-letter-${idx}-btn`}
            >
              <FileText className="h-3 w-3" /> View Superseded PDF
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

